import { create } from 'zustand';

/** Per-device choices, kept in this browser only (a laptop and a phone have different mics). */
interface DevicePrefs {
  micId: string;
  speakerId: string;
  cameraId: string;
  /**
   * Named after what runs: deepfilter (DeepFilterNet3, also cuts keyboard and mouse clicks), rnnoise,
   * gtcrn (newer, may handle room echo better), all three in the browser (lib/denoise.ts); browser:
   * WebRTC's own suppression; off. Echo cancellation always stays on.
   */
  denoise: Denoise;
  /** Automatic gain: louder quiet voices, but also louder room tone and echo tails. */
  autoGain: boolean;
  /** A soft cue when someone else turns their mic on. */
  othersMicCue: boolean;
  set(patch: Partial<Omit<DevicePrefs, 'set'>>): void;
}

const KEY = 'novachat.devices';
const DENOISE = ['deepfilter', 'rnnoise', 'gtcrn', 'browser', 'off'] as const;
export type Denoise = (typeof DENOISE)[number];
const defaults = { micId: 'default', speakerId: 'default', cameraId: 'default', denoise: 'rnnoise' as Denoise, autoGain: true, othersMicCue: true };

/** A saved mode; RNNoise and GTCRN were saved as 'strong' and 'experimental' before the modes were named after their models. */
function denoiseOf(saved: unknown): Denoise {
  const renamed = saved === 'strong' ? 'rnnoise' : saved === 'experimental' ? 'gtcrn' : saved;
  return DENOISE.find((mode) => mode === renamed) ?? defaults.denoise;
}

function load(): typeof defaults {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Record<keyof typeof defaults, unknown>>;
    return { ...defaults, ...(saved as Partial<typeof defaults>), denoise: denoiseOf(saved.denoise) };
  } catch {
    return defaults;
  }
}

export const useDevices = create<DevicePrefs>()((set, get) => ({
  ...load(),
  set(patch) {
    set(patch);
    const { micId, speakerId, cameraId, denoise, autoGain, othersMicCue } = get();
    try {
      localStorage.setItem(KEY, JSON.stringify({ micId, speakerId, cameraId, denoise, autoGain, othersMicCue }));
    } catch {
      // private mode or storage disabled: the choice lasts for this tab only
    }
  },
}));
